<template>
  <div class="container-fluid">
    <!-- Header with breadcrumb-style navigation -->
    <div class="mb-4">
      <div class="d-flex align-items-center">
        <button 
          class="btn btn-link text-muted p-0 me-2"
          @click="router.push('/databank')"
          style="text-decoration: none;"
        >
          Databank
        </button>
        <span class="text-muted me-2">/</span>
        <h2 class="mb-0">{{ $t('databank.certificates.title') }}</h2>
      </div>
    </div>

    <!-- Alert Messages -->
    <div v-if="error" class="alert alert-danger alert-dismissible fade show" role="alert">
      {{ error }}
      <button type="button" class="btn-close" @click="error = null"></button>
    </div>
    
    <div v-if="success" class="alert alert-success alert-dismissible fade show" role="alert">
      {{ success }}
      <button type="button" class="btn-close" @click="success = null"></button>
    </div>

    <!-- Data Table -->
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <div class="d-flex align-items-center gap-3">
              <h5 class="mb-0">
                {{ $t('databank.certificates.existingRecords') }}
              </h5>
              <div class="position-relative" style="width: 250px;">
                <input
                  type="text"
                  class="form-control form-control-sm pe-4"
                  v-model="searchQuery"
                  :placeholder="$t('common.actions.search')"
                />
                <button
                  v-if="searchQuery"
                  type="button"
                  class="btn-close btn-close-sm position-absolute top-50 end-0 translate-middle-y me-2"
                  style="font-size: 0.7rem; opacity: 0.6;"
                  @click="clearSearch"
                  :title="$t('common.clear')"
                ></button>
              </div>
            </div>
            <div class="d-flex gap-2">
              <button 
                class="btn btn-primary"
                @click="openAddModal"
              >
                <i class="ph-duotone ph-plus me-2"></i>
                {{ $t('databank.certificates.addTitle') }}
              </button>
              <button class="btn btn-outline-primary" @click="loadCertificates()">
                <i class="ph-duotone ph-arrow-clockwise"></i>
              </button>
            </div>
          </div>
          <div class="card-body">
            <div v-if="isLoadingData" class="text-center py-4">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">{{ $t('common.loading') }}</span>
              </div>
            </div>
            
            <div v-else-if="filteredCertificates.length === 0" class="text-center py-4 text-muted">
              <i class="ph-duotone ph-package text-muted" style="font-size: 2rem;"></i>
              <p class="mb-0">{{ $t('databank.certificates.noRecords') }}</p>
            </div>
            
            <div v-else class="table-responsive">
              <table class="table table-hover">
                <thead class="table-light">
                  <tr>
                    <th>{{ $t('databank.certificates.codeFunction') }}</th>
                    <th>{{ $t('databank.certificates.nameFunction') }}</th>
                    <th>{{ $t('databank.certificates.intervalFunction') }}</th>
                    <th>{{ $t('common.createdAt') }}</th>
                    <th>{{ $t('common.actionsLabel') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="certificate in filteredCertificates" :key="certificate.id">
                    <td>
                      <code v-if="certificate.code">{{ certificate.code }}</code>
                      <span v-else class="text-muted">-</span>
                    </td>
                    <td>
                      <strong>{{ certificate.name }}</strong>
                    </td>
                    <td>
                      <span v-if="certificate.interval">{{ certificate.interval }}</span>
                      <span v-else class="text-muted">-</span>
                    </td>
                    <td>
                      <small class="text-muted">{{ formatDate(certificate.createdAt) }}</small>
                    </td>
                    <td>
                      <div class="d-flex gap-2">
                        <button 
                          class="btn btn-sm btn-outline-primary"
                          @click="editCertificate(certificate)"
                          :title="$t('common.edit')"
                        >
                          <i class="ph-duotone ph-pencil-simple"></i>
                        </button>
                        <button 
                          class="btn btn-sm btn-outline-danger"
                          @click="deleteCertificate(certificate.id)"
                          :title="$t('common.delete')"
                        >
                          <i class="ph-duotone ph-x"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Add/Edit Certificate Modal -->
    <div class="modal fade" id="certificateModal" tabindex="-1" aria-labelledby="certificateModalLabel" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="certificateModalLabel">
              {{ isEditing ? $t('databank.certificates.editTitle') : $t('databank.certificates.addTitle') }}
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="submitForm">
              <div class="row">
                <!-- Code Function -->
                <div class="col-md-6 mb-3">
                  <label class="form-label">
                    {{ $t('databank.certificates.codeFunction') }}
                  </label>
                  <input
                    type="text"
                    class="form-control"
                    v-model="form.code"
                    :placeholder="$t('databank.certificates.codePlaceholder')"
                  />
                </div>

                <!-- Name Function -->
                <div class="col-md-6 mb-3">
                  <label class="form-label">
                    {{ $t('databank.certificates.nameFunction') }}
                    <span class="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    class="form-control"
                    v-model="form.name"
                    :placeholder="$t('databank.certificates.namePlaceholder')"
                    :class="{ 'is-invalid': v$.name.$error }"
                    required
                  />
                  <div v-if="v$.name.$error" class="invalid-feedback">
                    {{ v$.name.$errors[0].$message }}
                  </div>
                </div>

                <!-- Interval Function -->
                <div class="col-md-6 mb-3">
                  <label class="form-label">
                    {{ $t('databank.certificates.intervalFunction') }}
                  </label>
                  <CultureNumberInput
                    v-model="form.interval"
                    :decimal-places="0"
                    :use-grouping="false"
                    :min="1"
                    class="form-control"
                    :class="{ 'is-invalid': v$.interval.$error }"
                    :placeholder="$t('databank.certificates.intervalPlaceholder')"
                  />
                  <div v-if="v$.interval.$error" class="invalid-feedback">
                    {{ v$.interval.$errors[0].$message }}
                  </div>
                </div>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
              {{ $t('common.cancel') }}
            </button>
            <button 
              type="button"
              class="btn btn-info"
              @click="handleSave(false)"
              :disabled="isLoading"
            >
              <span v-if="isLoading" class="spinner-border spinner-border-sm me-1"></span>
              <i class="ph-duotone ph-floppy-disk me-2"></i>
              {{ isLoading ? $t('common.saving') : $t('common.save') }}
            </button>
            <button 
              type="button"
              class="btn btn-primary"
              @click="handleSave(true)"
              :disabled="isLoading"
            >
              <span v-if="isLoading" class="spinner-border spinner-border-sm me-1"></span>
              <i class="ph-duotone ph-floppy-disk me-2"></i>
              {{ isLoading ? $t('common.savingAndClosing') : $t('common.saveAndClose') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import useVuelidate from '@vuelidate/core';
import { required, integer, minValue } from '@vuelidate/validators';
import certificateService from '@/services/certificateService';
import { Modal } from 'bootstrap';
import CultureNumberInput from '@/components/CultureNumberInput.vue';

const store = useStore();
const router = useRouter();
const { t } = useI18n();

const error = ref(null);
const success = ref(null);
const isLoading = ref(false);
const isLoadingData = ref(false);
const isEditing = ref(false);
const editingId = ref(null);
const searchQuery = ref('');
let certificateModal = null;

const form = ref({
  code: '',
  name: '',
  interval: null
});

const certificates = ref([]);

const currentUser = computed(() => store.getters['auth/currentUser']);

// Validation rules
const rules = computed(() => ({
  name: { required },
  interval: {
    integer,
    minValue: minValue(1)
  }
}));

const v$ = useVuelidate(rules, form);

// Use certificates directly since search is handled by API
const filteredCertificates = computed(() => {
  return certificates.value;
});

// Watch for search query changes
let searchTimeout;
watch(searchQuery, (newQuery) => {
  clearTimeout(searchTimeout);
  searchTimeout = setTimeout(() => {
    if (newQuery.trim()) {
      loadCertificates(newQuery.trim());
    } else {
      loadCertificates();
    }
  }, 500); // 500ms debounce
});

onMounted(() => {
  // Check if user is super admin
  if (!currentUser.value?.isSuperAdmin) {
    store.dispatch('notify', {
      message: t('common.notification.unauthorized'),
      type: 'error'  
    });
    router.push('/databank');
  }
  
  // Initialize modal
  const modalElement = document.getElementById('certificateModal');
  if (modalElement) {
    certificateModal = new Modal(modalElement);
  }
  
  loadCertificates();
});

const loadCertificates = async (searchText = '') => {
  isLoadingData.value = true;
  
  try {
    let response;
    
    // Handle case where event object is passed instead of string
    const searchQuery = typeof searchText === 'string' ? searchText : '';
    
    if (searchQuery) {
      // Use search API when there's a search query
      response = await certificateService.searchCertificates({
        searchText: searchQuery,
        page: 0,
        pageSize: 100
      });
    } else {
      // Load all certificates
      response = await certificateService.getCertificates();
    }
    
    if (response && Array.isArray(response)) {
      certificates.value = response.map(cert => ({
        id: cert.id,
        code: cert.code,
        name: cert.name,
        interval: cert.interval || cert.interval,
        createdAt: cert.creationTime || cert.createdAt
      }));
    } else if (response && response.items) {
      // Handle paginated response
      certificates.value = response.items.map(cert => ({
        id: cert.id,
        code: cert.code,
        name: cert.name,
        interval: cert.interval || cert.interval,
        createdAt: cert.creationTime || cert.createdAt
      }));
    } else {
      certificates.value = [];
    }
    
  } catch (err) {
    console.error('Error loading certificates:', err);
    error.value = t('databank.certificates.errors.loadError');
    
    store.dispatch('notify', {
      message: error.value,
      type: 'error'
    });
  } finally {
    isLoadingData.value = false;
  }
};

const openAddModal = () => {
  resetForm();
  if (certificateModal) {
    certificateModal.show();
  }
};

const handleSave = async (shouldClose = false) => {
  // Validate form
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;
  
  isLoading.value = true;
  let savedSuccessfully = false;
  let savedCertificate = null;
  error.value = null;
  success.value = null;
  
  try {
    const certificateData = {
      code: form.value.code || null,
      name: form.value.name,
      interval: form.value.interval
    };
    
    if (isEditing.value) {
      // Update existing certificate
      const response = await certificateService.updateCertificate(editingId.value, certificateData);
      
      if (response) {
        savedCertificate = response;
        // Update local array
        const index = certificates.value.findIndex(c => c.id === editingId.value);
        if (index !== -1) {
          certificates.value[index] = {
            ...certificates.value[index],
            code: response.code,
            name: response.name,
            interval: response.interval || response.interval
          };
        }
        success.value = t('databank.certificates.updateSuccess');
      }
    } else {
      // Create new certificate
      const response = await certificateService.createCertificate(certificateData);
      
      if (response) {
        savedCertificate = response;
        // Add to local array
        const newCertificate = {
          id: response.id,
          code: response.code,
          name: response.name,
          interval: response.interval || response.interval,
          createdAt: response.creationTime || new Date()
        };
        certificates.value.unshift(newCertificate);
        success.value = t('databank.certificates.createSuccess');
      }
    }
    
    savedSuccessfully = true;
    
    store.dispatch('notify', {
      message: success.value,
      type: 'success'
    });
    
    // If closing, hide the modal
    if (shouldClose && certificateModal) {
      certificateModal.hide();
      resetForm();
    }
    
  } catch (err) {
    console.error('Submit error:', err);
    error.value = t('databank.certificates.errors.saveError');
    
    store.dispatch('notify', {
      message: error.value,
      type: 'error'
    });
  } finally {
    isLoading.value = false;
  }
  
  // Post-Save Actions (only if save was successful and modal is staying open)
  if (savedSuccessfully && !shouldClose) {
    if (!isEditing.value && savedCertificate) {
      // If creating and NOT closing, switch to edit mode for the newly created certificate
      form.value = {
        code: savedCertificate.code || '',
        name: savedCertificate.name || '',
        interval: savedCertificate.interval || null
      };
      isEditing.value = true;
      editingId.value = savedCertificate.id;
    }
  }
};

const editCertificate = (certificate) => {
  form.value = {
    code: certificate.code || '',
    name: certificate.name || '',
    interval: certificate.interval || null
  };
  isEditing.value = true;
  editingId.value = certificate.id;
  
  if (certificateModal) {
    certificateModal.show();
  }
};

const deleteCertificate = async (id) => {
  if (!confirm(t('databank.certificates.confirmDelete'))) return;
  
  try {
    await certificateService.deleteCertificate(id);
    
    certificates.value = certificates.value.filter(c => c.id !== id);
    
    store.dispatch('notify', {
      message: t('databank.certificates.deleteSuccess'),
      type: 'success'
    });
    
  } catch (err) {
    console.error('Delete error:', err);
    store.dispatch('notify', {
      message: t('databank.certificates.errors.deleteError'),
      type: 'error'
    });
  }
};

const resetForm = () => {
  form.value = {
    code: '',
    name: '',
    interval: null
  };
  isEditing.value = false;
  editingId.value = null;
  v$.value.$reset();
};

const formatDate = (date) => {
  return new Date(date).toLocaleDateString();
};

const clearSearch = () => {
  searchQuery.value = '';
};
</script>

<style scoped>
.card-header {
  background: white;
  color: #212529;
}

.card-header h5,
.card-header h6,
.card-header i,
.card-header * {
  color: #212529 !important;
}

.card-header .form-control {
  color: #212529 !important;
}

.btn-link {
  color: #6c757d !important;
  font-size: 1.25rem;
  font-weight: 500;
}

.btn-link:hover {
  color: #495057 !important;
}
</style>

